import { supabase } from "@/lib/supabase";
import type { DocumentEntityLink, InsertDocumentEntityLink, MastermindDocument } from "@shared/schema";

export type EntityType = "space" | "topic" | "thread";

export interface LinkedEntity {
  id: string;
  type: EntityType;
  name: string;
  linkId: string;
  createdAt: string;
}

export interface SourceDocument extends MastermindDocument {
  linkedEntities?: LinkedEntity[];
}

export const documentLinkingService = {
  async getLinksForDocument(documentId: string): Promise<DocumentEntityLink[]> {
    const { data, error } = await supabase
      .from("document_entity_links")
      .select("id, document_id, entity_type, entity_id, link_type, created_at")
      .eq("document_id", documentId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching document links:", error);
      return [];
    }

    return (data || []).map((row: Record<string, unknown>) => ({
      id: row.id as string,
      documentId: row.document_id as string,
      entityType: row.entity_type as string,
      entityId: row.entity_id as string,
      linkType: row.link_type as string | null,
      createdAt: row.created_at as Date,
    }));
  },

  async getLinksForEntity(entityType: EntityType, entityId: string): Promise<DocumentEntityLink[]> {
    const { data, error } = await supabase
      .from("document_entity_links")
      .select("id, document_id, entity_type, entity_id, link_type, created_at")
      .eq("entity_type", entityType)
      .eq("entity_id", entityId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching entity links:", error);
      return [];
    }

    return (data || []).map((row: Record<string, unknown>) => ({
      id: row.id as string,
      documentId: row.document_id as string,
      entityType: row.entity_type as string,
      entityId: row.entity_id as string,
      linkType: row.link_type as string | null,
      createdAt: row.created_at as Date,
    }));
  },

  /**
   * Fetch linked document IDs/titles only for lightweight manifest use-cases.
   * Keeps entity-link order so UI/prompts can present stable numbering.
   */
  async getLinkedDocumentTitlesForEntity(
    entityType: EntityType,
    entityId: string
  ): Promise<Array<{ id: string; title: string }>> {
    const links = await this.getLinksForEntity(entityType, entityId);
    if (links.length === 0) return [];

    const documentIds = links.map((link) => link.documentId);
    const { data, error } = await supabase
      .from("mastermind_documents")
      .select("id, title")
      .in("id", documentIds);

    if (error) {
      console.error("Error fetching linked document titles:", error);
      return [];
    }

    const byId = new Map(
      (data || []).map((row: Record<string, unknown>) => [
        row.id as string,
        {
          id: row.id as string,
          title: (row.title as string) || "Untitled",
        },
      ])
    );

    return documentIds
      .map((id) => byId.get(id))
      .filter((value): value is { id: string; title: string } => value != null);
  },

  async getLinkedDocumentsForEntity(entityType: EntityType, entityId: string): Promise<MastermindDocument[]> {
    const links = await this.getLinksForEntity(entityType, entityId);
    if (links.length === 0) return [];

    const documentIds = links.map(link => link.documentId);
    
    const { data, error } = await supabase
      .from("mastermind_documents")
      .select("id, user_id, space_id, topic_id, title, type, content, url, folder_id, google_docs_id, attachments, is_source, metadata, is_locked, locked_at, created_at, updated_at")
      .in("id", documentIds);

    if (error) {
      console.error("Error fetching linked documents:", error);
      return [];
    }

    return (data || []).map((row: Record<string, unknown>) => ({
      id: row.id as string,
      userId: row.user_id as string,
      spaceId: row.space_id as string | null,
      topicId: row.topic_id as string | null,
      title: row.title as string,
      type: row.type as string,
      content: row.content as string | null,
      url: row.url as string | null,
      folderId: row.folder_id as string | null,
      googleDocsId: row.google_docs_id as string | null,
      attachments: row.attachments,
      isSource: row.is_source as boolean | null,
      metadata: row.metadata,
      isLocked: row.is_locked as boolean | null,
      lockedAt: row.locked_at as Date | null,
      createdAt: row.created_at as Date,
      updatedAt: row.updated_at as Date,
    }));
  },

  async getSourceDocuments(userId?: string, spaceId?: string): Promise<MastermindDocument[]> {
    let query = supabase
      .from("mastermind_documents")
      .select("id, user_id, space_id, topic_id, title, type, content, url, folder_id, google_docs_id, attachments, is_source, metadata, is_locked, locked_at, created_at, updated_at")
      .eq("is_source", true);

    if (userId) {
      query = query.eq("user_id", userId);
    }

    if (spaceId) {
      query = query.eq("space_id", spaceId);
    }

    const { data, error } = await query.order("updated_at", { ascending: false });

    if (error) {
      console.error("Error fetching source documents:", error);
      return [];
    }

    return (data || []).map((row: Record<string, unknown>) => ({
      id: row.id as string,
      userId: row.user_id as string,
      spaceId: row.space_id as string | null,
      topicId: row.topic_id as string | null,
      title: row.title as string,
      type: row.type as string,
      content: row.content as string | null,
      url: row.url as string | null,
      folderId: row.folder_id as string | null,
      googleDocsId: row.google_docs_id as string | null,
      attachments: row.attachments,
      isSource: row.is_source as boolean | null,
      metadata: row.metadata,
      isLocked: row.is_locked as boolean | null,
      lockedAt: row.locked_at as Date | null,
      createdAt: row.created_at as Date,
      updatedAt: row.updated_at as Date,
    }));
  },

  async createLink(link: InsertDocumentEntityLink): Promise<DocumentEntityLink | null> {
    const existingLink = await supabase
      .from("document_entity_links")
      .select("id")
      .eq("document_id", link.documentId)
      .eq("entity_type", link.entityType)
      .eq("entity_id", link.entityId)
      .maybeSingle();

    if (existingLink.data) {
      console.log("Link already exists");
      return null;
    }

    const { data, error } = await supabase
      .from("document_entity_links")
      .insert({
        document_id: link.documentId,
        entity_type: link.entityType,
        entity_id: link.entityId,
        link_type: link.linkType || "reference",
      })
      .select()
      .single();

    if (error) {
      // DS EDIT 7: unique index uq_document_entity_links_triplet (073) makes
      // concurrent duplicate links impossible — treat the violation exactly
      // like the pre-check's "already exists" path, not as a failure.
      if ((error as { code?: string }).code === "23505") {
        console.log("Link already exists");
        return null;
      }
      console.error("Error creating link:", error);
      return null;
    }

    return {
      id: data.id,
      documentId: data.document_id,
      entityType: data.entity_type,
      entityId: data.entity_id,
      linkType: data.link_type,
      createdAt: data.created_at,
    };
  },

  /**
   * DS EDIT 7 — explicit thread↔document link (one junction row IS both
   * directions; unique-enforced at the DB). Documents = mastermind_documents.
   */
  async linkDocumentToThread(documentId: string, threadId: string): Promise<DocumentEntityLink | null> {
    return this.createLink({ documentId, entityType: "thread", entityId: threadId });
  },

  /** DS EDIT 7 — remove the explicit thread↔document link (both directions at once). */
  async unlinkDocumentFromThread(documentId: string, threadId: string): Promise<boolean> {
    return this.deleteLinkByDocumentAndEntity(documentId, "thread", threadId);
  },

  async deleteLink(linkId: string): Promise<boolean> {
    const { error } = await supabase
      .from("document_entity_links")
      .delete()
      .eq("id", linkId);

    if (error) {
      console.error("Error deleting link:", error);
      return false;
    }

    return true;
  },

  async deleteLinkByDocumentAndEntity(
    documentId: string,
    entityType: EntityType,
    entityId: string
  ): Promise<boolean> {
    const { error } = await supabase
      .from("document_entity_links")
      .delete()
      .eq("document_id", documentId)
      .eq("entity_type", entityType)
      .eq("entity_id", entityId);

    if (error) {
      console.error("Error deleting link:", error);
      return false;
    }

    return true;
  },

  async toggleDocumentAsSource(documentId: string, isSource: boolean): Promise<boolean> {
    const { error } = await supabase
      .from("mastermind_documents")
      .update({ is_source: isSource })
      .eq("id", documentId);

    if (error) {
      console.error("Error toggling source status:", error);
      throw new Error(`Failed to update source status: ${error.message}`);
    }

    return true;
  },

  async getLinkedEntitiesForDocument(documentId: string): Promise<LinkedEntity[]> {
    const links = await this.getLinksForDocument(documentId);
    if (links.length === 0) return [];

    const entities: LinkedEntity[] = [];

    const spaceLinks = links.filter(l => l.entityType === "space");
    const topicLinks = links.filter(l => l.entityType === "topic");
    const threadLinks = links.filter(l => l.entityType === "thread");

    if (spaceLinks.length > 0) {
      const { data: spaces } = await supabase
        .from("mastermind_spaces")
        .select("id, name")
        .in("id", spaceLinks.map(l => l.entityId));

      if (spaces) {
        for (const space of spaces) {
          const link = spaceLinks.find(l => l.entityId === space.id);
          if (link) {
            entities.push({
              id: space.id,
              type: "space",
              name: space.name,
              linkId: link.id,
              createdAt: link.createdAt?.toString() || "",
            });
          }
        }
      }
    }

    if (topicLinks.length > 0) {
      const { data: topics } = await supabase
        .from("topics")
        .select("id, name")
        .in("id", topicLinks.map(l => l.entityId));

      if (topics) {
        for (const topic of topics) {
          const link = topicLinks.find(l => l.entityId === topic.id);
          if (link) {
            entities.push({
              id: topic.id,
              type: "topic",
              name: topic.name,
              linkId: link.id,
              createdAt: link.createdAt?.toString() || "",
            });
          }
        }
      }
    }

    if (threadLinks.length > 0) {
      const { data: threads } = await supabase
        .from("chat_threads")
        .select("id, title")
        .in("id", threadLinks.map(l => l.entityId));

      if (threads) {
        for (const thread of threads) {
          const link = threadLinks.find(l => l.entityId === thread.id);
          if (link) {
            entities.push({
              id: thread.id,
              type: "thread",
              name: thread.title || "Untitled Thread",
              linkId: link.id,
              createdAt: link.createdAt?.toString() || "",
            });
          }
        }
      }
    }

    return entities;
  },

  async searchEntities(
    query: string,
    entityType?: EntityType,
    excludeDocumentId?: string
  ): Promise<LinkedEntity[]> {
    const entities: LinkedEntity[] = [];
    const searchTerm = `%${query.toLowerCase()}%`;

    if (!entityType || entityType === "space") {
      const { data: spaces } = await supabase
        .from("mastermind_spaces")
        .select("id, name")
        .ilike("name", searchTerm)
        .limit(10);

      if (spaces) {
        for (const space of spaces) {
          entities.push({
            id: space.id,
            type: "space",
            name: space.name,
            linkId: "",
            createdAt: "",
          });
        }
      }
    }

    if (!entityType || entityType === "topic") {
      const { data: topics } = await supabase
        .from("topics")
        .select("id, name")
        .ilike("name", searchTerm)
        .limit(10);

      if (topics) {
        for (const topic of topics) {
          entities.push({
            id: topic.id,
            type: "topic",
            name: topic.name,
            linkId: "",
            createdAt: "",
          });
        }
      }
    }

    if (!entityType || entityType === "thread") {
      const { data: threads } = await supabase
        .from("chat_threads")
        .select("id, title")
        .ilike("title", searchTerm)
        .limit(10);

      if (threads) {
        for (const thread of threads) {
          entities.push({
            id: thread.id,
            type: "thread",
            name: thread.title || "Untitled Thread",
            linkId: "",
            createdAt: "",
          });
        }
      }
    }

    if (excludeDocumentId) {
      const existingLinks = await this.getLinksForDocument(excludeDocumentId);
      const existingEntityIds = new Set(existingLinks.map(l => l.entityId));
      return entities.filter(e => !existingEntityIds.has(e.id));
    }

    return entities;
  },
};
